import { useEffect, useRef, useState } from "react";
import api, { API, apiErrorMessage } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Wallet, CheckCircle2, AlertCircle, Upload, KeyRound, FileLock2, ShieldCheck, Trash2, ExternalLink, RefreshCw } from "lucide-react";

export default function SuperAdminWallet() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ pass_type_id: "", team_id: "", p12_password: "" });
  const [p12File, setP12File] = useState(null);
  const [wwdrFile, setWwdrFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const p12Ref = useRef(null);
  const wwdrRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/wallet/apple/status");
      setStatus(data);
      if (data.configured) {
        setForm({
          pass_type_id: data.pass_type_id || "",
          team_id: data.team_id || "",
          p12_password: "",
        });
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!p12File || !wwdrFile) { toast.error("Both certificate files are required"); return; }
    if (!form.pass_type_id || !form.team_id || !form.p12_password) {
      toast.error("Pass Type ID, Team ID and password are required");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("pass_type_id", form.pass_type_id);
      fd.append("team_id", form.team_id);
      fd.append("p12_password", form.p12_password);
      fd.append("p12", p12File);
      fd.append("wwdr", wwdrFile);
      const token = localStorage.getItem("mr_token");
      const res = await fetch(`${API}/super-admin/wallet/apple/credentials`, {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Upload failed");
      }
      toast.success("Apple Wallet credentials saved");
      setP12File(null);
      setWwdrFile(null);
      setForm({ ...form, p12_password: "" });
      if (p12Ref.current) p12Ref.current.value = "";
      if (wwdrRef.current) wwdrRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, err.message || "Upload failed"));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove Apple Wallet credentials? Patients will no longer be able to add their pass to Wallet until you upload again.")) return;
    try {
      await api.delete("/super-admin/wallet/apple/credentials");
      toast.success("Credentials removed");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not remove credentials"));
    }
  };

  const toggleLiveUpdates = async (next) => {
    try {
      const fd = new FormData();
      fd.append("enabled", String(next));
      const token = localStorage.getItem("mr_token");
      const res = await fetch(`${API}/super-admin/wallet/apple/live-updates`, {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Toggle failed");
      }
      toast.success(next ? "Live updates enabled" : "Live updates disabled");
      await load();
    } catch (err) {
      toast.error(err.message || "Could not toggle");
    }
  };

  const expiringSoon = (() => {
    if (!status?.cert_expires_at) return false;
    const days = (new Date(status.cert_expires_at) - new Date()) / (1000 * 60 * 60 * 24);
    return days > 0 && days < 30;
  })();

  return (
    <AdminShell scope="super" title="Apple Wallet">
      <div className="max-w-3xl space-y-8">
        <div>
          <span className="label-eyebrow text-mr-accent">Wallet passes</span>
          <h2 className="font-display text-3xl font-bold text-mr-primary mt-2">Apple Wallet integration</h2>
          <p className="text-mr-secondary mt-2">Upload your Pass Type ID certificate so patients can save their MedRewards card to Apple Wallet. You'll need an active Apple Developer Program membership.</p>
        </div>

        {/* Status card */}
        <div className="bg-white border border-mr rounded-2xl p-6">
          <div className="flex items-start gap-3">
            {loading ? (
              <div className="h-10 w-10 rounded-full bg-mr-bg animate-pulse" />
            ) : status?.configured ? (
              <div className="h-10 w-10 rounded-full bg-mr-success/15 text-mr-success flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5" />
              </div>
            ) : (
              <div className="h-10 w-10 rounded-full bg-mr-accent/15 text-mr-accent flex items-center justify-center">
                <AlertCircle className="h-5 w-5" />
              </div>
            )}
            <div className="flex-1">
              <p className="font-display text-lg font-semibold text-mr-primary" data-testid="wallet-status">
                {loading ? "Checking…" : status?.configured ? "Apple Wallet is configured" : "Apple Wallet is not configured"}
              </p>
              <p className="text-sm text-mr-secondary mt-1">
                {loading ? "" : status?.configured ? "Patients can add their membership card to Apple Wallet." : "Upload your Pass Type ID certificate below to enable wallet passes."}
              </p>
              {status?.configured && (
                <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-mr-secondary">Pass Type ID</span><p className="font-mono text-mr-primary">{status.pass_type_id}</p></div>
                  <div><span className="text-mr-secondary">Team ID</span><p className="font-mono text-mr-primary">{status.team_id}</p></div>
                  <div><span className="text-mr-secondary">Certificate</span><p className="text-mr-primary truncate">{status.cert_common_name || "—"}</p></div>
                  <div>
                    <span className="text-mr-secondary">Expires</span>
                    <p className="text-mr-primary inline-flex items-center gap-2">
                      {status.cert_expires_at ? new Date(status.cert_expires_at).toLocaleDateString() : "—"}
                      {expiringSoon && <Badge className="bg-mr-accent text-white">Expires soon</Badge>}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {status?.configured && (
              <Button data-testid="wallet-remove-btn" variant="ghost" size="sm" onClick={remove} className="text-mr-accent hover:text-mr-accent hover:bg-mr-accent/10">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Live updates toggle */}
        {status?.configured && (
          <div className="bg-white border border-mr rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-full bg-mr-accent/10 text-mr-accent flex items-center justify-center flex-shrink-0">
                <RefreshCw className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-display text-lg font-semibold text-mr-primary">Live pass updates</p>
                    <p className="text-sm text-mr-secondary mt-1">When ON, points/tier changes auto-refresh in patients' Wallet via APNs push. When OFF, passes are static and patients re-download to see new balances.</p>
                  </div>
                  <Switch
                    data-testid="wallet-live-updates-toggle"
                    checked={!!status.live_updates_enabled}
                    onCheckedChange={toggleLiveUpdates}
                  />
                </div>
                {!status.live_updates_enabled && (
                  <div className="mt-3 text-xs text-mr-secondary bg-mr-bg rounded-lg px-3 py-2 border border-mr">
                    <b className="text-mr-primary">Currently disabled</b> — recommended default. iOS does stricter validation when live updates are on; enable only after verifying the basic pass install works on a real iPhone.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Setup guide */}
        <details className="bg-mr-bg border border-mr rounded-2xl">
          <summary className="px-6 py-4 cursor-pointer font-display font-semibold text-mr-primary inline-flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-mr-accent" /> Step-by-step setup guide
          </summary>
          <div className="px-6 pb-6 text-sm text-mr-secondary space-y-3">
            <ol className="list-decimal pl-5 space-y-2">
              <li>Enrol in the <a className="text-mr-accent underline" href="https://developer.apple.com/programs/" target="_blank" rel="noreferrer">Apple Developer Program <ExternalLink className="inline h-3 w-3" /></a> ($99/year, business verification can take 1–3 days).</li>
              <li>In Certificates, IDs & Profiles → <b>Identifiers</b>, create a <b>Pass Type ID</b> (e.g. <code className="bg-white px-1 py-0.5 rounded">pass.app.medrewards.membership</code>).</li>
              <li>Note your <b>Team ID</b> (10-char code in the top-right of developer.apple.com).</li>
              <li>Generate a CSR on macOS (Keychain Access → Certificate Assistant → Request a Certificate from a Certificate Authority).</li>
              <li>Create a <b>Pass Type ID Certificate</b>, upload the CSR, download the .cer. Double-click to install in Keychain.</li>
              <li>In Keychain, find the cert + its private key, right-click → <b>Export</b> → save as <b>.p12</b> with a password.</li>
              <li>Download the latest <a className="text-mr-accent underline" href="https://www.apple.com/certificateauthority/" target="_blank" rel="noreferrer">Apple WWDR intermediate certificate (G4 or newer)<ExternalLink className="inline h-3 w-3" /></a>.</li>
              <li>Upload both files below, along with the Pass Type ID, Team ID, and .p12 password.</li>
            </ol>
            <p className="mt-3 text-xs">Your <b>.p12</b> and password are stored encrypted at rest. They never leave the backend — the frontend only sends them via this upload.</p>
          </div>
        </details>

        {/* Upload form */}
        <form onSubmit={submit} className="bg-white border border-mr rounded-2xl p-6 space-y-5">
          <div>
            <h3 className="font-display text-lg font-semibold text-mr-primary inline-flex items-center gap-2">
              <Upload className="h-4 w-4" /> {status?.configured ? "Update credentials" : "Upload credentials"}
            </h3>
            <p className="text-sm text-mr-secondary mt-1">All four fields below are required.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Pass Type ID</Label>
              <Input
                data-testid="wallet-pass-type-id"
                required
                value={form.pass_type_id}
                onChange={(e) => setForm({ ...form, pass_type_id: e.target.value })}
                placeholder="pass.app.medrewards.membership"
                className="mt-2 font-mono text-sm"
              />
            </div>
            <div>
              <Label>Team ID</Label>
              <Input
                data-testid="wallet-team-id"
                required
                value={form.team_id}
                onChange={(e) => setForm({ ...form, team_id: e.target.value })}
                placeholder="ABCDE12345"
                className="mt-2 font-mono text-sm uppercase tracking-wide"
                maxLength={10}
              />
            </div>
          </div>

          <div>
            <Label className="inline-flex items-center gap-2"><FileLock2 className="h-3.5 w-3.5" /> Pass Type ID certificate (.p12)</Label>
            <Input
              ref={p12Ref}
              data-testid="wallet-p12-file"
              type="file"
              accept=".p12"
              onChange={(e) => setP12File(e.target.files?.[0] || null)}
              className="mt-2 cursor-pointer"
            />
            {p12File && <p className="text-xs text-mr-secondary mt-1">{p12File.name} · {Math.round(p12File.size / 1024)} KB</p>}
          </div>

          <div>
            <Label className="inline-flex items-center gap-2"><KeyRound className="h-3.5 w-3.5" /> .p12 password</Label>
            <Input
              data-testid="wallet-p12-password"
              type="password"
              required
              value={form.p12_password}
              onChange={(e) => setForm({ ...form, p12_password: e.target.value })}
              placeholder="Password you set when exporting from Keychain"
              className="mt-2"
            />
          </div>

          <div>
            <Label className="inline-flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Apple WWDR intermediate (.cer or .pem)</Label>
            <Input
              ref={wwdrRef}
              data-testid="wallet-wwdr-file"
              type="file"
              accept=".cer,.pem,.crt"
              onChange={(e) => setWwdrFile(e.target.files?.[0] || null)}
              className="mt-2 cursor-pointer"
            />
            {wwdrFile && <p className="text-xs text-mr-secondary mt-1">{wwdrFile.name} · {Math.round(wwdrFile.size / 1024)} KB</p>}
          </div>

          <Button
            data-testid="wallet-save-btn"
            type="submit"
            disabled={saving}
            className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]"
          >
            <Wallet className="h-4 w-4 mr-2" />
            {saving ? "Saving…" : status?.configured ? "Replace credentials" : "Save credentials"}
          </Button>
        </form>
      </div>
    </AdminShell>
  );
}
